import type {
    Characteristic,
    RuleCondition,
    RuleConditionLeaf,
} from '../schema';
import type { SelectionsMap } from '../state/selections';
import type { EvaluatedMap } from '../eval/types';
import { evaluateOperator } from './operators';

/** Characteristic types whose value is computed rather than picked. */
const CALCULATED_TYPES: ReadonlySet<string> = new Set([
    'expression',
    'combi',
    'matrixvalue',
    'productattribute',
    'webservice',
]);

export interface AltTitleIndex {
    /**
     * Map of `alt_title` → Characteristic. Built once per parsed Component
     * (alt_titles are stable across selections).
     */
    byAltTitle: Map<string, Characteristic>;
}

/**
 * Evaluate a Rule's condition tree against the current selections. AND/OR
 * groups are nested; leaves resolve the referenced Characteristic by
 * alt_title and apply the operator.
 *
 * Treats missing/unknown Characteristic references as `false` for that
 * leaf (rule doesn't match) rather than throwing — keeps the engine
 * resilient to component edits.
 */
export function evaluateCondition(
    condition: RuleCondition | undefined,
    selections: SelectionsMap,
    index: AltTitleIndex,
    evaluated?: EvaluatedMap,
): boolean {
    if (!condition) return false;
    return evaluateGroup(condition.condition, condition.rules, selections, index, evaluated);
}

function evaluateGroup(
    op: 'AND' | 'OR',
    leaves: ReadonlyArray<RuleConditionLeaf>,
    selections: SelectionsMap,
    index: AltTitleIndex,
    evaluated: EvaluatedMap | undefined,
): boolean {
    if (leaves.length === 0) return op === 'AND'; // empty AND = vacuously true, empty OR = false

    if (op === 'AND') {
        return leaves.every((leaf) => evaluateLeaf(leaf, selections, index, evaluated));
    }
    return leaves.some((leaf) => evaluateLeaf(leaf, selections, index, evaluated));
}

function evaluateLeaf(
    leaf: RuleConditionLeaf,
    selections: SelectionsMap,
    index: AltTitleIndex,
    evaluated: EvaluatedMap | undefined,
): boolean {
    // A leaf can itself be a nested group.
    if (leaf.condition && leaf.rules) {
        return evaluateGroup(leaf.condition, leaf.rules, selections, index, evaluated);
    }

    const characteristic = index.byAltTitle.get(leaf.id) ?? index.byAltTitle.get(leaf.field);
    if (!characteristic) return false;

    // Calculated Characteristics (formula, combination, matrix, external
    // values) have no selection — their condition reads the computed result,
    // as legacy does by evaluating the condition as a formula (rules/rule.js).
    const left = evaluated && CALCULATED_TYPES.has(characteristic.type)
        ? (evaluated.get(characteristic.id) ?? null)
        : resolveSelectionValue(characteristic, selections);
    return evaluateOperator(leaf.operator, left, leaf.value);
}

/**
 * Pick the value to feed into operator-evaluation for a Characteristic.
 * Shape depends on the selection kind:
 *
 * - `single` → the picked Value's id (or `null`)
 * - `multi`  → array of picked Value ids
 * - `text`   → the typed string
 * - `quantities` → the sum of all quantities (rules in legacy data check
 *   "non-empty" via `is_not_null`)
 *
 * When nothing is selected: returns `null` for single/quantities, `''`
 * for text, `[]` for multi. Operator coercion handles those uniformly.
 */
function resolveSelectionValue(
    characteristic: Characteristic,
    selections: SelectionsMap,
): unknown {
    const sel = selections[characteristic.id];
    if (!sel) {
        switch (characteristic.type) {
            case 'text':
            case 'area':
                return '';
            case 'multiselect':
            case 'checkbox':
            case 'listimage':
                return [];
            case 'multiquantity':
                return 0;
            default:
                return null;
        }
    }

    switch (sel.kind) {
        case 'single':
            return sel.valueId;
        case 'multi':
            return [...sel.valueIds];
        case 'text':
            return sel.value;
        case 'quantities':
            return Object.values(sel.quantities).reduce((sum, q) => sum + q, 0);
    }
}

export function buildAltTitleIndex(characteristics: Iterable<Characteristic>): AltTitleIndex {
    const byAltTitle = new Map<string, Characteristic>();
    for (const c of characteristics) {
        if (c.altTitle) byAltTitle.set(c.altTitle, c);
    }
    return { byAltTitle };
}
